import * as React from "react"
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { cn } from "@/lib/utils"

export type V2Point = { label: string; net_value: number | null; highlight: boolean }

// Unit-aware axis/tooltip formatting: IDR compacts to T/M/jt, pct shows "%",
// qty shows a grouped integer. Mirrors the design-system chart tokens.
function makeFormatters(unit: string) {
  const axis = (value: number): string => {
    if (unit === "pct") return `${value.toFixed(0)}%`
    if (unit === "qty") return new Intl.NumberFormat("id-ID", { notation: "compact" }).format(value)
    const abs = Math.abs(value)
    if (abs >= 1e12) return `${(value / 1e12).toFixed(1)} T`
    if (abs >= 1e9) return `${(value / 1e9).toFixed(1)} M`
    if (abs >= 1e6) return `${(value / 1e6).toFixed(0)} jt`
    return new Intl.NumberFormat("id-ID").format(Math.round(value))
  }
  const full = (value: number): string => {
    if (unit === "pct") return `${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(value)}%`
    if (unit === "qty") return `${new Intl.NumberFormat("id-ID").format(Math.round(value))} unit`
    return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(value)
  }
  return { axis, full }
}

function HighlightDot(props: { cx?: number; cy?: number; index?: number; payload?: V2Point }) {
  const { cx, cy, index, payload } = props
  if (cx == null || cy == null) return <g key={index} />
  if (payload?.highlight) {
    return (
      <circle key={index} cx={cx} cy={cy} r={5} fill="var(--color-accent)" stroke="var(--color-surface)" strokeWidth={2} />
    )
  }
  return <circle key={index} cx={cx} cy={cy} r={0} fill="none" />
}

/**
 * SSR-safe, unit-aware 12-month trend line for the v2 assistant. The queried month
 * is highlighted with a dashed accent reference line + dot. ResponsiveContainer is
 * gated behind a post-hydration flag with a stable placeholder.
 */
export function V2Chart({
  data,
  unit = "idr",
  label = "Nilai",
  height = 260,
  className,
}: {
  data: V2Point[]
  unit?: string
  label?: string
  height?: number
  className?: string
}) {
  const [mounted, setMounted] = React.useState(false)
  React.useEffect(() => setMounted(true), [])
  const { axis, full } = React.useMemo(() => makeFormatters(unit), [unit])
  const highlight = data.find((d) => d.highlight)?.label

  if (data.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border border-dashed border-hairline text-sm text-ink-muted",
          className,
        )}
        style={{ height }}
      >
        Tidak ada data tren.
      </div>
    )
  }

  if (!mounted) {
    return <div className={cn("animate-pulse rounded-lg bg-surface", className)} style={{ height }} aria-hidden="true" />
  }

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 12, right: 16, bottom: 0, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-hairline)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: "var(--color-ink-muted)", fontSize: 11 }}
            stroke="var(--color-hairline)"
            tickMargin={8}
          />
          <YAxis
            tickFormatter={axis}
            tick={{ fill: "var(--color-ink-muted)", fontSize: 11 }}
            stroke="var(--color-hairline)"
            width={64}
          />
          <Tooltip
            formatter={(value) => [typeof value === "number" ? full(value) : (value as React.ReactNode), label]}
            contentStyle={{
              backgroundColor: "var(--color-surface)",
              border: "1px solid var(--color-hairline)",
              borderRadius: "0.5rem",
              color: "var(--color-ink-body)",
              fontSize: 12,
            }}
            labelStyle={{ color: "var(--color-ink-display)", fontWeight: 600 }}
            cursor={{ stroke: "var(--color-ink-muted)", strokeWidth: 1 }}
          />
          {highlight && (
            <ReferenceLine
              x={highlight}
              stroke="var(--color-accent)"
              strokeDasharray="4 4"
              strokeWidth={1.5}
              label={{ value: "ditanya", position: "top", fill: "var(--color-accent)", fontSize: 10 }}
            />
          )}
          <Line
            type="monotone"
            dataKey="net_value"
            name={label}
            stroke="var(--color-accent)"
            strokeWidth={2}
            dot={<HighlightDot />}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
